import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select/creatable';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { DrawerBody, OverlayDrawer } from '@fluentui/react-components';
import { Dismiss20Regular } from '@fluentui/react-icons';
import { PostTask } from '../../redux/taskSlice';
import { GetDataroomItemInfo, GetFolderDefaults } from '../../redux/dataroomSlice';
import styles from './TaskFormSidebar.module.css';

const TaskFormSidebar = ({ show, setShow, selectedRow, setSelectedRow, defaultTask, setDefaultData }) => {
    const dispatch = useDispatch();
    const { projectUsersAll } = useSelector(state => state.team);
    const { user } = useSelector(state => state.user);
    const { taskButtonLoading, projectWorkstreams, projectTags, taskData } = useSelector(state => state.task);
    const { workstreams, default_folders, items } = useSelector(state => state.dataroom);
    const [selectedAssignee, setSelectedAssignee] = React.useState();
    const [task, setTask] = React.useState('');
    const [showMoreDetails, setShowMoreDetails] = React.useState(false);
    const [dataroomTags, setDataroomTags] = React.useState([]);
    const [formData, setFormData] = React.useState({
        deadline: '',
        project: localStorage['project_id'],
        status: 'NOT_STARTED',
        priority: '',
        workstream_repr: '',
        tag_repr: '',
    });
    // Get all workstreams from different sources (same as WorkstreamManagementMenu)
    const getAllWorkstreams = () => {
        const workstreamMap = new Map();

        // 1. Project workstreams
        if (projectWorkstreams && projectWorkstreams.length > 0) {
            projectWorkstreams.forEach(ws => {
                workstreamMap.set(ws.name, { ...ws, source: 'project' });
            });
        }

        // 2. Dataroom workstreams
        if (workstreams && workstreams.length > 0) {
            workstreams.forEach(ws => {
                workstreamMap.set(ws.name, { ...ws, source: 'dataroom' });
            });
        }

        // 3. All workstreams from task data
        const getAllWorkstreamsFromTaskData = () => {
            // Handle both array and object structures
            let tasks = [];
            if (Array.isArray(taskData)) {
                tasks = taskData;
            } else if (taskData && taskData.data && Array.isArray(taskData.data)) {
                tasks = taskData.data;
            } else if (taskData && taskData.groups && Array.isArray(taskData.groups)) {
                // Handle grouped data - flatten all groups
                tasks = taskData.groups.flatMap(group => group.tasks || []);
            } else {
                return [];
            }

            return tasks
                .map(task => task.workstream_repr)
                .filter(workstream => workstream && workstream.trim() !== '')
                .filter((workstream, index, arr) => arr.indexOf(workstream) === index); // Remove duplicates
        };

        const taskWorkstreamNames = getAllWorkstreamsFromTaskData();
        taskWorkstreamNames.forEach(workstreamName => {
            if (!workstreamMap.has(workstreamName)) {
                workstreamMap.set(workstreamName, {
                    id: `task_${workstreamName}`,
                    name: workstreamName,
                    source: 'task',
                });
            }
        });

        return Array.from(workstreamMap.values());
    };

    // Check if selected workstream is from dataroom
    const isDataroomWorkstream = formData.workstream_repr && workstreams?.some(ws => ws.name === formData.workstream_repr);

    // Fetch dataroom data when workstream changes
    const fetchDataroomData = React.useCallback(() => {
        if (isDataroomWorkstream && formData.workstream_repr) {
            // Get the workstream object
            const selectedWorkstream = workstreams?.find(ws => ws.name === formData.workstream_repr) ||
                projectWorkstreams?.find(ws => ws.name === formData.workstream_repr) || {
                    id: `custom_${formData.workstream_repr}`,
                    name: formData.workstream_repr,
                    source: 'custom',
                };

            if (selectedWorkstream?.id) {
                dispatch(GetDataroomItemInfo(selectedWorkstream.id));
                dispatch(GetFolderDefaults(selectedWorkstream.name));
            }
        }
    }, [isDataroomWorkstream, formData.workstream_repr, workstreams, projectWorkstreams, dispatch]);

    // Update dataroom tags when default_folders or items change
    React.useEffect(() => {
        if (isDataroomWorkstream) {
            const combinedOptions = [...new Set([...default_folders.map(folder => folder?.ws_folder), ...items.map(item => item?.name)])].map(label => ({
                id: `dataroom_${label}`,
                name: label,
                source: 'dataroom',
            }));
            setDataroomTags(combinedOptions);
        } else {
            setDataroomTags([]);
        }
    }, [default_folders, items, isDataroomWorkstream]);

    // Fetch dataroom data when workstream changes
    React.useEffect(() => {
        if (formData.workstream_repr) {
            fetchDataroomData();
        }
    }, [formData.workstream_repr, fetchDataroomData]);

    // Get all tags (same as TagManagementMenu)
    const getAllTags = () => {
        const tagMap = new Map();

        // 1. Dataroom tags (when workstream is from dataroom)
        if (isDataroomWorkstream && dataroomTags.length > 0) {
            dataroomTags.forEach(tag => {
                tagMap.set(tag.name, { ...tag, source: 'dataroom' });
            });
        }

        // 2. Project tags
        if (projectTags && projectTags.length > 0) {
            projectTags.forEach(tag => {
                tagMap.set(tag.name, { ...tag, source: 'project' });
            });
        }

        // 3. All tags from task data
        const getAllTagsFromTaskData = () => {
            // Handle both array and object structures
            let tasks = [];
            if (Array.isArray(taskData)) {
                tasks = taskData;
            } else if (taskData && taskData.data && Array.isArray(taskData.data)) {
                tasks = taskData.data;
            } else if (taskData && taskData.groups && Array.isArray(taskData.groups)) {
                // Handle grouped data - flatten all groups
                tasks = taskData.groups.flatMap(group => group.tasks || []);
            } else {
                return [];
            }

            return tasks
                .map(task => task.tag_repr)
                .filter(tag => tag && tag.trim() !== '')
                .filter((tag, index, arr) => arr.indexOf(tag) === index); // Remove duplicates
        };

        const taskTagNames = getAllTagsFromTaskData();
        taskTagNames.forEach(tagName => {
            if (!tagMap.has(tagName)) {
                tagMap.set(tagName, {
                    id: `task_${tagName}`,
                    name: tagName,
                    source: 'task',
                });
            }
        });

        return Array.from(tagMap.values());
    };

    const handleSubmit = async () => {
        if (showMoreDetails) {
            // Expanded view - include all fields
            let body = {
                ...formData,
                task,
                created_by: user?.id,
                workstream_repr: formData?.workstream_repr,
                tag_repr: formData?.tag_repr,
            };
            if (selectedAssignee) {
                if (selectedAssignee?.__isNew__) body.assigned_to_offline = selectedAssignee?.value;
                else body.assigned_to = selectedAssignee?.value?.user_id;
            }
            if (body.deadline === '') delete body.deadline;
            await dispatch(PostTask(body));
        } else {
            // Simplified view - only essential fields like task management table
            let body = {
                task,
                project: localStorage['project_id'],
            };
            await dispatch(PostTask(body));
        }
        handleClose();
    };

    const handleClose = () => {
        setShow(false);
        setSelectedRow && setSelectedRow(null);
        setFormData({
            deadline: '',
            assigned_to: '',
            project: localStorage['project_id'],
            status: 'NOT_STARTED',
            priority: '',
            workstream_repr: '',
            tag_repr: '',
        });
        setSelectedAssignee();
        setTask('');
        setShowMoreDetails(false);
        setDefaultData && setDefaultData();
    };

    React.useEffect(() => {
        if (defaultTask) setTask(defaultTask);
    }, [defaultTask]);

    const allWorkstreams = getAllWorkstreams();
    const allTags = getAllTags();

    return (
        <>
            <OverlayDrawer style={{ minWidth: 400 }} type="overlay" position="end" open={show} onOpenChange={(_, { open }) => handleClose(open)}>
                <DrawerBody style={{ padding: 16 }}>
                    <div className="">
                        <div className="d-flex justify-content-between">
                            <p className={styles.sidebarTitle}>Add New Task</p>
                            <Dismiss20Regular role="button" onClick={handleClose} color="var(--Neutral-Gray-6)" />
                        </div>

                        <div className="mt-4">
                            {/* Task Description - Always visible */}
                            <div className="mb-4">
                                <textarea
                                    className="form-control"
                                    placeholder="Type your task here..."
                                    value={task}
                                    onChange={e => setTask(e.target.value)}
                                    rows={4}
                                    style={{
                                        border: '1px solid #e0e0e0',
                                        borderRadius: '8px',
                                        padding: '12px',
                                        fontSize: '14px',
                                        resize: 'vertical',
                                        minHeight: '100px',
                                    }}
                                />
                            </div>

                            {/* Action Buttons - Only show when not in expanded view */}
                            {!showMoreDetails && (
                                <div className="d-flex justify-content-between align-items-center">
                                    <button
                                        disabled={!task.trim() || taskButtonLoading}
                                        onClick={handleSubmit}
                                        className="btn btn-primary"
                                        style={{
                                            backgroundColor: 'var(--Brand-Secondary)',
                                            borderRadius: '6px',
                                            padding: '8px 16px',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            border: 'none',
                                        }}>
                                        Add Task {taskButtonLoading && <AiOutlineLoading3Quarters className="rotateLoader" />}
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => setShowMoreDetails(true)}
                                        className="btn btn-link"
                                        style={{
                                            color: 'var(--Brand-Secondary)',
                                            textDecoration: 'none',
                                            fontSize: '14px',
                                        }}>
                                        Add more details
                                    </button>
                                </div>
                            )}

                            {/* Expanded Form Fields */}
                            {showMoreDetails && (
                                <div className="mt-4">
                                    {/* Owner Field */}
                                    <div className="mb-3">
                                        <label className="form-label" style={{ fontSize: '12px', fontWeight: '500', marginBottom: '3px' }}>
                                            Owner
                                        </label>
                                        <Select
                                            name="selectedAssignee"
                                            options={projectUsersAll?.map(item => {
                                                return { label: item?.value?.full_name, value: item?.value };
                                            })}
                                            className="basic-multi-select"
                                            classNamePrefix="select"
                                            value={selectedAssignee}
                                            onChange={e => setSelectedAssignee(e)}
                                            placeholder="Select team member"
                                            maxMenuHeight={200}
                                            isClearable
                                            styles={{
                                                control: (provided, state) => ({
                                                    ...provided,
                                                    borderColor: '#e0e0e0',
                                                    borderRadius: '6px',
                                                    minHeight: '36px',
                                                    fontSize: 14,
                                                }),
                                                valueContainer: (provided, state) => ({
                                                    ...provided,
                                                    height: '36px',
                                                    padding: '0 8px',
                                                }),
                                                input: (provided, state) => ({
                                                    ...provided,
                                                    margin: '0px',
                                                }),
                                                indicatorSeparator: state => ({
                                                    display: 'none',
                                                }),
                                                indicatorsContainer: (provided, state) => ({
                                                    ...provided,
                                                    height: '34px',
                                                }),
                                            }}
                                        />
                                    </div>

                                    {/* Priority and Status Row */}
                                    <div className="row mb-3">
                                        <div className="col-6">
                                            <label className="form-label" style={{ fontSize: '12px', fontWeight: '500', marginBottom: '3px' }}>
                                                Priority
                                            </label>
                                            <select
                                                name="priority"
                                                value={formData.priority}
                                                onChange={e => setFormData({ ...formData, priority: e.target.value })}
                                                className="form-select"
                                                style={{
                                                    borderColor: '#e0e0e0',
                                                    borderRadius: '6px',
                                                    fontSize: '14px',
                                                    height: '36px',
                                                }}>
                                                <option value="">Select priority</option>
                                                <option value="HIGH">High</option>
                                                <option value="MEDIUM">Medium</option>
                                                <option value="LOW">Low</option>
                                            </select>
                                        </div>
                                        <div className="col-6">
                                            <label className="form-label" style={{ fontSize: '12px', fontWeight: '500', marginBottom: '3px' }}>
                                                Status
                                            </label>
                                            <select
                                                name="status"
                                                value={formData.status}
                                                onChange={e => setFormData({ ...formData, status: e.target.value })}
                                                className="form-select"
                                                style={{
                                                    borderColor: '#e0e0e0',
                                                    borderRadius: '6px',
                                                    fontSize: '14px',
                                                    height: '36px',
                                                }}>
                                                <option value="NOT_STARTED">Not Started</option>
                                                <option value="WORKING_ON_IT">Working on it</option>
                                                <option value="BLOCKED">Blocked</option>
                                                <option value="PAUSED">Paused</option>
                                                <option value="COMPLETED">Completed</option>
                                                <option value="CANCELLED">Cancelled</option>
                                            </select>
                                        </div>
                                    </div>

                                    {/* Workstream Field */}
                                    <div className="mb-3">
                                        <label className="form-label" style={{ fontSize: '12px', fontWeight: '500', marginBottom: '3px' }}>
                                            Workstream
                                        </label>
                                        <select
                                            name="workstream_repr"
                                            value={formData.workstream_repr}
                                            onChange={e => setFormData({ ...formData, workstream_repr: e.target.value })}
                                            className="form-select"
                                            style={{
                                                borderColor: '#e0e0e0',
                                                borderRadius: '6px',
                                                fontSize: '14px',
                                                height: '36px',
                                            }}>
                                            <option value="">Select Workstream</option>
                                            {allWorkstreams?.map(item => (
                                                <option key={item?.id} value={item?.name}>
                                                    {item?.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Tag Field */}
                                    <div className="mb-3">
                                        <label className="form-label" style={{ fontSize: '12px', fontWeight: '500', marginBottom: '3px' }}>
                                            Tag
                                        </label>
                                        <select
                                            name="tag_repr"
                                            value={formData.tag_repr}
                                            onChange={e => setFormData({ ...formData, tag_repr: e.target.value })}
                                            className="form-select"
                                            style={{
                                                borderColor: '#e0e0e0',
                                                borderRadius: '6px',
                                                fontSize: '14px',
                                                height: '36px',
                                            }}>
                                            <option value="">Select Tag</option>
                                            {allTags?.map(item => (
                                                <option key={item?.id} value={item?.name}>
                                                    {item?.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Deadline Row */}
                                    <div className="row mb-4">
                                        <div className="col-6">
                                            <label className="form-label" style={{ fontSize: '12px', fontWeight: '500', marginBottom: '3px' }}>
                                                Deadline
                                            </label>
                                            <input
                                                type="date"
                                                name="deadline"
                                                value={formData.deadline}
                                                onChange={e => setFormData({ ...formData, deadline: e.target.value })}
                                                className="form-control"
                                                style={{
                                                    borderColor: '#e0e0e0',
                                                    borderRadius: '6px',
                                                    fontSize: '14px',
                                                    height: '36px',
                                                }}
                                            />
                                        </div>
                                    </div>

                                    {/* Action Buttons for Expanded View */}
                                    <div className="d-flex justify-content-between align-items-center">
                                        <button
                                            disabled={!task.trim() || taskButtonLoading}
                                            onClick={handleSubmit}
                                            className="btn btn-primary"
                                            style={{
                                                backgroundColor: 'var(--Brand-Secondary)',
                                                borderRadius: '6px',
                                                padding: '8px 16px',
                                                fontSize: '14px',
                                                fontWeight: '500',
                                                border: 'none',
                                            }}>
                                            Add Task {taskButtonLoading && <AiOutlineLoading3Quarters className="rotateLoader" />}
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() => setShowMoreDetails(false)}
                                            className="btn btn-link"
                                            style={{
                                                color: 'var(--Brand-Secondary)',
                                                textDecoration: 'none',
                                                fontSize: '14px',
                                                fontWeight: '500',
                                            }}>
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </DrawerBody>
            </OverlayDrawer>
        </>
    );
};

export default TaskFormSidebar;
